import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, Github, Linkedin, Mouse, Sparkles } from 'lucide-react';
import { personal } from '../data/portfolio';

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  padding: 140px 24px 80px;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

const Wrap = styled.div`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  align-items: center;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1.15fr 0.85fr;
    gap: 80px;
  }
`;

const Left = styled.div`
  position: relative;
  z-index: 2;
`;

const Eyebrow = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 13px;
  margin-bottom: 22px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.success};
    box-shadow: 0 0 12px ${({ theme }) => theme.colors.success};
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(44px, 7vw, 82px);
  font-weight: 700;
  margin-bottom: 18px;

  .grad {
    background: linear-gradient(135deg, #A78BFA 0%, #7C5CFF 45%, #22D3EE 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

const Role = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.mono};
  color: ${({ theme }) => theme.colors.primarySoft};
  font-size: 18px;
  margin-bottom: 22px;
  letter-spacing: 0.02em;
`;

const Tagline = styled(motion.p)`
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 17px;
  max-width: 540px;
  margin-bottom: 34px;
`;

const Actions = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border-radius: ${({ theme }) => theme.radii.pill};
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.08);
  box-shadow: ${({ theme }) => theme.shadows.glow};
  transition: transform 200ms ease, box-shadow 200ms ease, background 200ms ease;

  &:hover {
    transform: translateY(-2px);
    background: #6947f5;
  }
`;

const GhostBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border-radius: ${({ theme }) => theme.radii.pill};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  transition: transform 200ms ease, background 200ms ease, border-color 200ms ease;

  &:hover {
    transform: translateY(-2px);
    background: ${({ theme }) => theme.colors.surfaceHi};
    border-color: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const Socials = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialLink = styled.a`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDim};
  cursor: pointer;
  transition: color 200ms ease, transform 200ms ease, border-color 200ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primarySoft};
    transform: translateY(-2px);
  }
`;

const Right = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 460px;
  order: -1;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    order: 0;
  }
`;

const BlobFrame = styled(motion.div)`
  position: relative;
  width: min(460px, 100%);
  aspect-ratio: 1 / 1.05;
  background: linear-gradient(135deg, #7C5CFF 0%, #22D3EE 100%);
  border-radius: 45% 55% 55% 45% / 40% 45% 55% 60%;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  box-shadow: 0 30px 80px rgba(124, 92, 255, 0.35);
`;

const BlobGrain = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(60% 40% at 30% 20%, rgba(255,255,255,0.28), transparent 70%),
    radial-gradient(50% 40% at 80% 90%, rgba(0,0,0,0.15), transparent 70%);
  pointer-events: none;
`;

const Avatar = styled.img`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center -40%;
  display: block;
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  z-index: 3;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(21,21,31,0.75)' : 'rgba(255,255,255,0.85)'};
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 14px 16px;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  font-size: 14px;

  strong {
    display: block;
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: 18px;
  }
  span { color: ${({ theme }) => theme.colors.textDim}; font-size: 12px; }
`;

const ScrollHint = styled(motion.a)`
  position: absolute;
  left: 24px;
  bottom: 30px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textDim};
  font-size: 13px;
  cursor: pointer;

  &:hover { color: ${({ theme }) => theme.colors.text}; }
`;

export default function Hero() {
  const go = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <Section id="home">
      <Wrap>
        <Left>
          <Eyebrow initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <span className="dot" /> Available for new projects
          </Eyebrow>
          <Title initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
            Hi, I'm <span className="grad">Darryl.</span>
          </Title>
          <Role initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            &lt; Full Stack Developer /&gt;
          </Role>
          <Tagline initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
            {personal.tagline}
          </Tagline>
          <Actions initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
            <PrimaryBtn href="#contact" onClick={go('contact')}>
              Contact Me <ArrowRight size={18} />
            </PrimaryBtn>
            <GhostBtn href={personal.resumeUrl} target="_blank" rel="noreferrer">
              <Sparkles size={16} /> View Resume
            </GhostBtn>
          </Actions>
          <Socials>
            <SocialLink href={personal.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <Linkedin size={18} />
            </SocialLink>
            <SocialLink href={personal.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub">
              <Github size={18} />
            </SocialLink>
          </Socials>
        </Left>

        <Right>
          <BlobFrame
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
              opacity: 1,
              scale: 1,
              borderRadius: [
                '45% 55% 55% 45% / 40% 45% 55% 60%',
                '55% 45% 40% 60% / 55% 40% 60% 45%',
                '45% 55% 55% 45% / 40% 45% 55% 60%',
              ],
            }}
            transition={{
              opacity: { duration: 0.6 },
              scale: { duration: 0.6 },
              borderRadius: { duration: 12, repeat: Infinity, ease: 'easeInOut' },
            }}
          >
            <Avatar src={personal.avatar} alt={`${personal.firstName} ${personal.lastName}`} />
            <BlobGrain />
          </BlobFrame>

          <FloatingCard
            style={{ left: '-6%', top: '18%' }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9 }}
          >
            <div>
              <strong>8+ Years</strong>
              <span>Building products</span>
            </div>
          </FloatingCard>

          <FloatingCard
            style={{ right: '-4%', bottom: '10%' }}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1 }}
          >
            <div>
              <strong>55+ Projects</strong>
              <span>Delivered end to end</span>
            </div>
          </FloatingCard>
        </Right>
      </Wrap>

      <ScrollHint href="#about" onClick={go('about')} animate={{ y: [0, 6, 0] }} transition={{ duration: 2, repeat: Infinity }}>
        <Mouse size={16} /> Scroll Down
      </ScrollHint>
    </Section>
  );
}
